import { Alert, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { authStore } from '../auth/authStore';
import { CLIENT_BRAND_NAME } from '../branding';
import { resolveClientEnv } from '../graphql/config/resolveClientEnv';
import { createOnboardingApi, type OnboardingApi } from './onboardingApi';
import type { OnboardingInputField } from './onboardingErrors';

export const INVALID_ONBOARDING_LINK_MESSAGE =
  'This onboarding link is invalid or no longer available. Ask your broker to show a new QR code and scan it again.';

const RETRYABLE_ERROR_MESSAGE = "We couldn't create your account. Please try again.";

const FORM_FIELDS: readonly OnboardingInputField[] = ['name', 'phone', 'email', 'password'];

const FIELD_LABELS: Record<OnboardingInputField, string> = {
  name: 'Name',
  phone: 'Phone',
  email: 'Email',
  password: 'Password',
};

const SERVER_INVALID_INPUT_MESSAGES: Record<OnboardingInputField, string> = {
  name: 'Please enter a valid name.',
  phone: 'Please enter a valid phone number.',
  email: 'Please enter a valid email address.',
  password: 'Please enter a valid password.',
};

type FormValues = Record<OnboardingInputField, string>;
type FormErrors = Partial<Record<OnboardingInputField, string>>;
type OnboardingConsumeApi = Pick<OnboardingApi, 'consumeToken'>;
type FocusTarget = OnboardingInputField | 'summary';

export type OnboardUserPageProps = {
  api?: OnboardingConsumeApi;
  apiFactory?: () => OnboardingConsumeApi;
};

const INITIAL_VALUES: FormValues = {
  name: '',
  phone: '',
  email: '',
  password: '',
};

const hasUsableToken = (token: string | null): token is string => token !== null && token.trim().length > 0;

const isValidEmail = (email: string): boolean => {
  const input = document.createElement('input');
  input.type = 'email';
  input.required = true;
  input.value = email;
  return input.checkValidity();
};

const validate = (values: FormValues): FormErrors => {
  const errors: FormErrors = {};
  const trimmedName = values.name.trim();
  const trimmedPhone = values.phone.trim();
  const trimmedEmail = values.email.trim();

  if (!trimmedName) errors.name = 'Name is required.';
  if (!trimmedPhone) errors.phone = 'Phone is required.';
  if (!trimmedEmail) {
    errors.email = 'Email is required.';
  } else if (!isValidEmail(trimmedEmail)) {
    errors.email = 'Enter a valid email address.';
  }
  if (!values.password.trim()) errors.password = 'Password is required.';

  return errors;
};

const firstInvalidField = (errors: FormErrors): OnboardingInputField | undefined =>
  FORM_FIELDS.find(field => errors[field] !== undefined);

export const OnboardUserPage = ({ api, apiFactory }: OnboardUserPageProps) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const validToken = hasUsableToken(token);
  const [values, setValues] = useState<FormValues>(INITIAL_VALUES);
  const [fieldErrors, setFieldErrors] = useState<FormErrors>({});
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [rescanRequired, setRescanRequired] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusRequest, setFocusRequest] = useState<{ target: FocusTarget; id: number } | null>(null);
  const fieldRefs = useRef<Record<OnboardingInputField, HTMLInputElement | null>>({
    name: null,
    phone: null,
    email: null,
    password: null,
  });
  const summaryRef = useRef<HTMLDivElement | null>(null);
  const apiRef = useRef<OnboardingConsumeApi | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const inFlightRef = useRef(false);
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);

  const requestFocus = (target: FocusTarget): void => {
    setFocusRequest(current => ({ target, id: (current?.id ?? 0) + 1 }));
  };

  const clearFormState = (): void => {
    setValues(INITIAL_VALUES);
    setFieldErrors({});
    setSubmissionError(null);
  };

  useEffect(() => {
    if (!focusRequest) return;

    if (focusRequest.target === 'summary') {
      summaryRef.current?.focus();
      return;
    }

    fieldRefs.current[focusRequest.target]?.focus();
  }, [focusRequest]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      requestIdRef.current += 1;
      abortControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    requestIdRef.current += 1;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    inFlightRef.current = false;
    setIsSubmitting(false);
    clearFormState();
    setRescanRequired(false);
  }, [token]);

  const getApi = (): OnboardingConsumeApi => {
    if (api) return api;

    apiRef.current ??= apiFactory?.() ?? createOnboardingApi({ env: resolveClientEnv() });
    return apiRef.current;
  };

  const setFieldRef = (field: OnboardingInputField) => (element: HTMLInputElement | null): void => {
    fieldRefs.current[field] = element;
  };

  const showRetryableError = (): void => {
    setSubmissionError(RETRYABLE_ERROR_MESSAGE);
    requestFocus('summary');
  };

  const handleChange = (field: OnboardingInputField) => (event: ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value;
    setValues(current => ({ ...current, [field]: value }));
    setFieldErrors(current => {
      if (!current[field]) return current;

      const remainingErrors = { ...current };
      delete remainingErrors[field];
      return remainingErrors;
    });
    setSubmissionError(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!validToken || inFlightRef.current) return;

    const errors = validate(values);
    const invalidField = firstInvalidField(errors);
    if (invalidField) {
      setFieldErrors(errors);
      setSubmissionError(null);
      requestFocus(invalidField);
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    const controller = new AbortController();
    abortControllerRef.current = controller;
    inFlightRef.current = true;
    setIsSubmitting(true);
    setFieldErrors({});
    setSubmissionError(null);

    try {
      const result = await getApi().consumeToken(
        {
          token,
          name: values.name.trim(),
          phone: values.phone.trim(),
          email: values.email.trim(),
          password: values.password.trim(),
        },
        { signal: controller.signal },
      );

      if (!mountedRef.current || requestId !== requestIdRef.current) return;

      switch (result.kind) {
        case 'success':
          authStore.setAuth({
            token: result.value.authJwt,
            agentId: result.value.agentId,
            brokerageId: result.value.brokerageId,
            role: 'agent',
          });
          navigate('/', { replace: true });
          return;
        case 'rescan-required':
          clearFormState();
          setRescanRequired(true);
          return;
        case 'invalid-input':
          setFieldErrors({ [result.field]: SERVER_INVALID_INPUT_MESSAGES[result.field] });
          requestFocus(result.field);
          return;
        case 'email-exists':
          setFieldErrors({ email: 'An account already exists for this email address.' });
          requestFocus('email');
          return;
        case 'invalid-request':
        case 'retryable':
          showRetryableError();
          return;
        default:
          showRetryableError();
          return;
      }
    } catch {
      if (!mountedRef.current || requestId !== requestIdRef.current) return;

      showRetryableError();
    } finally {
      if (requestId === requestIdRef.current) {
        inFlightRef.current = false;
        abortControllerRef.current = null;
        if (mountedRef.current) setIsSubmitting(false);
      }
    }
  };

  const showInvalidLinkState = !validToken || rescanRequired;

  return (
    <Box component="main" sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center', minHeight: '100vh', p: { xs: 2, sm: 3 } }}>
      <Paper elevation={3} sx={{ p: { xs: 3, sm: 4 }, width: '100%', maxWidth: 480 }}>
        <Stack spacing={3}>
          <Box>
            <Typography color="primary" variant="overline">
              {CLIENT_BRAND_NAME}
            </Typography>
            <Typography component="h1" variant="h4">
              Join {CLIENT_BRAND_NAME}
            </Typography>
          </Box>
          {showInvalidLinkState ? (
            <Alert severity="error">{INVALID_ONBOARDING_LINK_MESSAGE}</Alert>
          ) : (
            <Box component="form" noValidate aria-busy={isSubmitting} aria-label={`Join ${CLIENT_BRAND_NAME}`} onSubmit={handleSubmit}>
              <Stack spacing={2}>
                {submissionError && (
                  <Alert ref={summaryRef} severity="error" tabIndex={-1}>
                    {submissionError}
                  </Alert>
                )}
                <TextField
                  autoComplete="name"
                  disabled={isSubmitting}
                  error={Boolean(fieldErrors.name)}
                  fullWidth
                  helperText={fieldErrors.name}
                  inputRef={setFieldRef('name')}
                  label={FIELD_LABELS.name}
                  onChange={handleChange('name')}
                  required
                  slotProps={{ inputLabel: { shrink: true } }}
                  type="text"
                  value={values.name}
                />
                <TextField
                  autoComplete="tel"
                  disabled={isSubmitting}
                  error={Boolean(fieldErrors.phone)}
                  fullWidth
                  helperText={fieldErrors.phone}
                  inputRef={setFieldRef('phone')}
                  label={FIELD_LABELS.phone}
                  onChange={handleChange('phone')}
                  required
                  slotProps={{ inputLabel: { shrink: true } }}
                  type="tel"
                  value={values.phone}
                />
                <TextField
                  autoComplete="email"
                  disabled={isSubmitting}
                  error={Boolean(fieldErrors.email)}
                  fullWidth
                  helperText={fieldErrors.email}
                  inputRef={setFieldRef('email')}
                  label={FIELD_LABELS.email}
                  onChange={handleChange('email')}
                  required
                  slotProps={{ inputLabel: { shrink: true } }}
                  type="email"
                  value={values.email}
                />
                <TextField
                  autoComplete="new-password"
                  disabled={isSubmitting}
                  error={Boolean(fieldErrors.password)}
                  fullWidth
                  helperText={fieldErrors.password}
                  inputRef={setFieldRef('password')}
                  label={FIELD_LABELS.password}
                  onChange={handleChange('password')}
                  required
                  slotProps={{ inputLabel: { shrink: true } }}
                  type="password"
                  value={values.password}
                />
                <Button disabled={isSubmitting} fullWidth type="submit" variant="contained">
                  {isSubmitting ? 'Creating account…' : 'Create account'}
                </Button>
              </Stack>
            </Box>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};
